import { useEffect, useReducer, useRef, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import {
  ArrowRight,
  Bot,
  CalendarClock,
  ClipboardList,
  Wallet,
} from "lucide-react";
import { api, type Analytics, type Kpi, type WorkRow } from "../api";
import { fmtDateTime, fmtDayMon, fmtMoney } from "../lib/format";
import { useFeature } from "../lib/use-tariff";
import { useBranch } from "../lib/use-branch";
import { calculateRevenueSummary } from "../lib/revenue-summary";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { EmptyState } from "../components/ui/empty-state";
import { Skeleton } from "../components/ui/skeleton";
import { Money, Plate, Mono } from "../components/ui/ledger";
import { homeAnalyticsReducer, initialHomeAnalyticsState } from "./home-analytics-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

// "1 234 567 so'm" → { num: "1 234 567", unit: "so'm" }, raqamni kattaroq,
// birlikni kichikroq qilib ko'rsatish uchun ajratamiz.
function splitMoney(v: number | string | null | undefined) {
  const s = fmtMoney(v);
  const i = s.lastIndexOf(" ");
  if (i === -1) return { num: s, unit: "" };
  return { num: s.slice(0, i), unit: s.slice(i + 1) };
}

export function HomePage({
  onOpenWorks,
  onOpenDebts,
}: {
  onOpenWorks: () => void;
  onOpenDebts: () => void;
}) {
  const { t } = useTranslation("home");
  const hasAnalytics = useFeature("analytics");
  // Qarz daftari yo'q tarifda unga olib boradigan yo'llar ham
  // ko'rsatilmaydi — aks holda bosilganda bosh sahifaga qaytarib tashlanadi.
  const hasDebts = useFeature("qarzlar");
  const hasProducts = useFeature("tovars");
  const { selectedBranchId } = useBranch();
  const [kpi, setKpi] = useState<Kpi | null>(null);
  const [recent, setRecent] = useState<WorkRow[]>([]);
  const [analyticsState, dispatchAnalytics] = useReducer(
    homeAnalyticsReducer,
    initialHomeAnalyticsState,
  );
  const analyticsRequestId = useRef(0);
  const [analyticsRetry, setAnalyticsRetry] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setErr(null);
      // Do not show the previous branch's values while this branch is loading.
      setKpi(null);
      setRecent([]);
      try {
        const [k, w] = await Promise.all([
          api.kpi(selectedBranchId),
          api.works({ page: 1, page_size: 10, branch_id: selectedBranchId ?? undefined }),
        ]);
        if (alive) {
          setKpi(k);
          setRecent(w.items);
        }
      } catch (e) {
        if (alive) {
          // Both values stay cleared when either half of the branch snapshot fails.
          setErr((e as { message?: string }).message || t("errorFallback"));
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [selectedBranchId]);

  // Daromad xulosasi faqat analitika ochiq tariflarda — alohida, sekin so'rov.
  useEffect(() => {
    if (!hasAnalytics) {
      dispatchAnalytics({ type: "reset" });
      return;
    }
    let alive = true;
    const requestId = ++analyticsRequestId.current;
    // Filial almashganda avvalgi filial xulosasini yangi so'rov ostida
    // ko'rsatmaymiz. `alive` esa kechikib kelgan eski javoblarni inkor qiladi.
    dispatchAnalytics({ type: "start", requestId });
    (async () => {
      try {
        const a = await api.analytics("month", selectedBranchId);
        if (!alive) return;
        dispatchAnalytics({ type: "success", requestId, data: a });
      } catch {
        if (alive) dispatchAnalytics({ type: "failure", requestId });
      }
    })();
    return () => {
      alive = false;
    };
  }, [hasAnalytics, selectedBranchId, analyticsRetry]);

  const debt = kpi ? Number(kpi.jami_debitorlik) : 0;

  return (
    <div className="home-page space-y-3.5">
      {err && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 text-sm">
          {err}
        </div>
      )}

      {/* ── KPI grid — bitta qator; qarz plitkasi tarifga qarab qo'shiladi ─── */}
      <div
        className={`home-kpi-grid grid grid-cols-1 sm:grid-cols-2 gap-3.5 ${
          hasDebts ? "lg:grid-cols-4" : "lg:grid-cols-3"
        }`}
      >
        {loading || !kpi
          ? Array.from({ length: hasDebts ? 4 : 3 }).map((_, i) => (
              <Skeleton key={i} className="h-[120px]" />
            ))
          : (
            <>
              {/* Bugungi daromad — to'liq aylanma; izoh Ish + Tovar headline'ga teng.
                  Omborsiz tarifda "Tovar" har doim 0 bo'lardi — ko'rsatilmaydi. */}
              <KpiCard
                label={t("kpi.revenueToday")}
                icon={<Wallet />}
                money={kpi.revenue_today}
                accent="blue"
                meta={
                  hasProducts
                    ? `${t("kpi.labor")} ${fmtMoney(kpi.labor_today)} · ${t("kpi.parts")} ${fmtMoney(kpi.parts_today)}`
                    : `${t("kpi.labor")} ${fmtMoney(kpi.labor_today)}`
                }
              />
              <KpiCard
                label={t("kpi.worksToday")}
                icon={<ClipboardList />}
                value={kpi.works_today}
                accent="orange"
                meta={t("kpi.worksWeekMeta", { count: kpi.works_week })}
              />
              <BotLinkTile kpi={kpi} />
              {hasDebts && (
                <KpiCard
                  label={t("kpi.receivableDebt")}
                  icon={<Wallet />}
                  money={kpi.jami_debitorlik}
                  accent="green"
                  warn={debt > 0}
                  onClick={onOpenDebts}
                  meta={t("kpi.debtBookLink")}
                />
              )}
            </>
          )}
      </div>

      {/* ── 30-day revenue summary ───────────────────────────── */}
      {kpi && !loading && (
        <div>
          {hasAnalytics ? (
            <RevenueSummary
              analytics={analyticsState.data}
              loading={analyticsState.status === "loading"}
              error={analyticsState.status === "error"}
              onRetry={() => setAnalyticsRetry((value) => value + 1)}
            />
          ) : (
            <Card className="flex flex-col justify-center">
              <CardContent className="p-6">
                <div className="text-sm text-muted-foreground">
                  <Trans
                    t={t}
                    i18nKey="chartLocked.text"
                    components={[<b className="text-foreground" />]}
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ── Recent works ─────────────────────────────────────── */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <div className="space-y-1">
            <CardTitle>{t("recent.title")}</CardTitle>
            <p className="text-xs text-muted-foreground">{t("recent.subtitle")}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onOpenWorks}>
            {t("recent.fullJournal")}
            <ArrowRight />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : recent.length === 0 ? (
            <EmptyState
              icon={<CalendarClock />}
              title={t("recent.emptyTitle")}
              description={t("recent.emptyDesc")}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("recent.table.date")}</TableHead>
                  <TableHead>{t("recent.table.client")}</TableHead>
                  <TableHead>{t("recent.table.car")}</TableHead>
                  <TableHead>{t("recent.table.service")}</TableHead>
                  <TableHead>{t("recent.table.master")}</TableHead>
                  <TableHead className="text-right">{t("recent.table.price")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recent.map((w) => (
                  <TableRow key={w.id}>
                    <TableCell>
                      <Mono className="text-muted-foreground">
                        {fmtDateTime(w.created_at)}
                      </Mono>
                    </TableCell>
                    <TableCell className="font-medium">{w.client_name}</TableCell>
                    <TableCell>
                      <Plate>{w.plate_number}</Plate>
                    </TableCell>
                    <TableCell>{w.service_name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {w.master_name}
                    </TableCell>
                    <TableCell className="text-right">
                      <Money tone="pos">{fmtMoney(w.price)}</Money>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ── KPI card ─────────────────────────────────────────────── */
function KpiCard({
  label,
  icon,
  value,
  money,
  meta,
  warn,
  onClick,
  accent = "blue",
}: {
  label: string;
  icon: React.ReactNode;
  value?: number | string;
  money?: number | string;
  meta?: React.ReactNode;
  warn?: boolean;
  onClick?: () => void;
  accent?: "blue" | "orange" | "indigo" | "green";
}) {
  const m = money != null ? splitMoney(money) : null;
  const content = (
    <>
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
        <span className="[&_svg]:size-3.5 [&_svg]:opacity-70">{icon}</span>
        {label}
      </div>
      <div
        className={
          "font-num text-[28px] font-bold leading-none tracking-tight tabular-nums " +
          (warn ? "text-warning" : "")
        }
      >
        {m ? (
          <>
            {m.num}
            {m.unit && (
              <span className="ml-1 text-[15px] font-semibold text-muted-foreground">
                {m.unit}
              </span>
            )}
          </>
        ) : (
          value
        )}
      </div>
      {meta && <div className="text-xs text-muted-foreground">{meta}</div>}
    </>
  );

  return (
    <Card
      className={
        `home-kpi home-kpi--${accent} overflow-hidden ` +
        (onClick ? "transition-colors hover:border-primary/40 " : "p-[18px] flex flex-col gap-2 ") +
        (warn ? "border-warning/40" : "")
      }
    >
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className="flex h-full w-full flex-col gap-2 p-[18px] text-left rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
        >
          {content}
        </button>
      ) : content}
    </Card>
  );
}

/* ── Botga ulangan plitkasi ───────────────────────────────────
 * Joriy holat (lifetime) metrikasi — sana oralig'iga bog'liq EMAS.
 * telegram_pct = with_telegram / total_clients (workshop scoped).
 * Yangi ustaxona (0 mijoz) uchun qo'rqitadigan qizil 0% emas, "—" ko'rsatamiz. */
function BotLinkTile({ kpi }: { kpi: Kpi }) {
  const { t } = useTranslation("home");
  const total = kpi.total_clients;
  const pct = Math.round(kpi.telegram_pct);
  // < 50% va mijozlar mavjud bo'lsa — ogohlantirish (amber) aksenti.
  const warn = total > 0 && kpi.telegram_pct < 50;
  return (
    <KpiCard
      label={t("kpi.botLinked")}
      icon={<Bot />}
      value={total > 0 ? `${pct}%` : "—"}
      accent="indigo"
      warn={warn}
      meta={
        total > 0
          ? t("kpi.botLinkedRatio", {
              linked: kpi.with_telegram,
              total,
            })
          : t("kpi.noClientsYet")
      }
    />
  );
}

/* ── Revenue summary (no graphical visualization) ─────────── */
function RevenueSummary({
  analytics,
  loading,
  error,
  onRetry,
}: {
  analytics: Analytics | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation("home");
  const summary = analytics ? calculateRevenueSummary(analytics) : null;

  return (
    <Card className="home-revenue-summary flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle>{t("revenueSummary.title")}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" aria-label={t("revenueSummary.loading")}>
            {Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-20" />)}
          </div>
        ) : error ? (
          <div className="flex min-h-20 flex-col items-center justify-center gap-3 text-sm text-muted-foreground" role="alert">
            <span>{t("revenueSummary.error")}</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              {t("revenueSummary.retry")}
            </Button>
          </div>
        ) : summary ? (
          <div className="home-revenue-metrics grid grid-cols-1 gap-px overflow-hidden rounded-lg border bg-border sm:grid-cols-2 lg:grid-cols-4">
            <SummaryMetric label={t("revenueSummary.total")} value={fmtMoney(summary.total)} />
            <SummaryMetric label={t("revenueSummary.dailyAverage")} value={fmtMoney(summary.dailyAverage)} />
            <SummaryMetric
              label={t("revenueSummary.bestDay")}
              value={summary.bestDay ? `${fmtDayMon(summary.bestDay.date)} · ${fmtMoney(summary.bestDay.amount)}` : "—"}
            />
            <SummaryMetric label={t("revenueSummary.lastSevenDays")} value={fmtMoney(summary.lastSevenDays)} />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function SummaryMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="home-revenue-metric min-w-0 bg-card p-4">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-2 font-num text-lg font-bold tabular-nums sm:text-xl">{value}</div>
    </div>
  );
}
